<script setup lang="ts">
import { ref, onBeforeMount, nextTick, watch, reactive, toValue } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { usePromosStore } from '@/store/PromosStore';
import { UserInfo } from '@/modules/user/UserInfo';
import { useUserInfo } from '@/modules/user/useUserInfo';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import routerKey from '@/router/routerKey';
import AppMenuItem from './AppMenuItem.vue';
import SidePromo from '@/components/promos/SidePromo.vue';

const { t } = useI18n({ useScope: 'global' });
const currentLanguage = getCurrentLocale().replace(/^\w/, (c) => c.toUpperCase());
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const currentUser = ref<UserInfo>();
const model = ref<any>([]);
const promosStore = usePromosStore();
const userInfo = useUserInfo();
const sidePromoInfo = ref([]);

const sectionTitle = reactive({
  sidePromoTitleEn: '',
  sidePromoTitleFr: ''
});

// This function is used to hide menu items that the current role does not have access to
const filterRoleNotValid = (group, permission) => {
  if (!!authorizations?.[group] && !authorizations?.[group][permission]) {
    return [toValue(currentUser)?.role];
  } else {
    return [];
  }
};

const buildMenu = () => {
  return [
    {
      label: '',
      roleNotValid: [],
      items: [
        {
          label: t('menu.group_travel_home'),
          roleNotValid: filterRoleNotValid('travel_home', 'menu_option_view'),
          icon: 'pi pi-home',
          to: { name: routerKey.HOME },
          activeRoute: [routerKey.HOME, routerKey.HOTEL, routerKey.AIR, routerKey.CRUISE]
        },
        {
          label: t('menu.my_quote'),
          icon: 'pi pi-table',
          roleNotValid: filterRoleNotValid('my_quote', 'menu_option_view'),
          to: { name: routerKey.MY_QUOTE }
        }
      ]
    },
    {
      label: t('menu.admin'),
      roleNotValid: filterRoleNotValid('configurationPage', 'admin_menu_option_view'),
      items: [
        {
          label: t('menu.quote_management'),
          icon: 'pi pi-table',
          to: { name: routerKey.QUOTE_MANAGEMENT },
          roleNotValid: filterRoleNotValid('quote_management', 'menu_option_view')
        },
        {
          label: t('menu.administration'),
          icon: 'pi pi-sliders-v',
          roleNotValid: filterRoleNotValid('configurationPage', 'menu_option_view'),
          items: [
            {
              label: t('menu.hotel'),
              icon: 'pi pi-building',
              to: { name: routerKey.HOTELS },
              roleNotValid: filterRoleNotValid('configurationPage', 'hotel_view'),
              class: 'pl-4'
            },
            {
              label: t('menu.hotel_contract'),
              icon: 'pi pi-building',
              to: { name: routerKey.HOTEL_CONTRACT },
              roleNotValid: filterRoleNotValid('configurationPage', 'hotel_contract_view'),
              class: 'pl-4'
            },
            {
              label: t('menu.airs'),
              icon: 'pi pi-cloud',
              to: { name: routerKey.AIRS },
              roleNotValid: filterRoleNotValid('configurationPage', 'air_view'),
              class: 'pl-4'
            },
            {
              label: t('menu.packages'),
              icon: 'pi pi-briefcase',
              to: { name: routerKey.PACKAGES },
              roleNotValid: filterRoleNotValid('configurationPage', 'package_view'),
              class: 'pl-4'
            },
            {
              label: t('menu.tours'),
              icon: 'pi pi-flag',
              to: { name: routerKey.TOURS },
              roleNotValid: filterRoleNotValid('configurationPage', 'tour_view'),
              class: 'pl-4'
            },
            {
              label: t('menu.cruises'),
              icon: 'pi pi-globe',
              to: { name: routerKey.CRUISE_MANAGEMENT },
              roleNotValid: filterRoleNotValid('configurationPage', 'cruise_view'),
              class: 'pl-4'
            },
            {
              label: t('menu.promos'),
              icon: 'pi pi-tags',
              to: { name: routerKey.PROMOS },
              roleNotValid: filterRoleNotValid('configurationPage', 'promos_view'),
              class: 'pl-4'
            },
            {
              label: t('menu.currency'),
              icon: 'pi pi-dollar',
              to: { name: routerKey.CURRENCY },
              roleNotValid: filterRoleNotValid('configurationPage', 'currency_view'),
              class: 'pl-4'
            },
            {
              label: t('menu.term_and_condition'),
              icon: 'pi pi-exclamation-circle',
              roleNotValid: filterRoleNotValid('configurationPage', 'term_condition_view'),
              to: { name: routerKey.TERMS_AND_CONDITIONS },
              class: 'pl-4'
            },
            {
              label: t('menu.quote_configuration'),
              icon: 'pi pi-tags',
              roleNotValid: filterRoleNotValid('configurationPage', 'configurable_items_view'), // will be changed key when BE change the key role
              to: { name: routerKey.QUOTE_CONFIGURATION },
              class: 'pl-4'
            },
            {
              label: t('menu.admin_setup'),
              icon: 'pi pi-envelope',
              roleNotValid: filterRoleNotValid('configurationPage', 'email_notification_view'), // will be changed key when BE change the key role
              to: { name: routerKey.ADMIN_SETUP },
              class: 'pl-4'
            },
            {
              label: t('menu.content_data_management'),
              icon: 'pi pi-sync',
              roleNotValid: filterRoleNotValid('configurationPage', 'data_synchronization_view'),
              to: { name: routerKey.SYNC_DATA },
              class: 'pl-4'
            },
            {
              label: t('menu.airline_management'),
              icon: 'pi pi-cloud',
              roleNotValid: filterRoleNotValid('configurationPage', 'airline_management_view'),
              to: { name: routerKey.FLIGHTS },
              class: 'pl-4'
            },
            {
              label: t('my_quote.group_type'),
              icon: 'pi pi-align-center',
              to: { name: routerKey.GROUP_TYPE },
              roleNotValid: filterRoleNotValid('configurationPage', 'group_type_management_view'),
              class: 'pl-4'
            },
            {
              label: t('menu.role_management'),
              icon: 'pi pi-lock',
              roleNotValid: filterRoleNotValid('configurationPage', 'role_update_view'),
              to: { name: routerKey.ROLES },
              class: 'pl-4'
            }
          ]
        },
        {
          label: t('menu.logs'),
          icon: 'pi pi-file',
          roleNotValid: filterRoleNotValid('configurationPage', 'logs_view'),
          items: [
            {
              label: t('menu.quote_history'),
              icon: 'pi pi-file',
              roleNotValid: [],
              to: { name: routerKey.LOGS },
              class: 'pl-6'
            },
            {
              label: t('menu.rules_history'),
              icon: 'pi pi-file',
              roleNotValid: [],
              to: { name: routerKey.RULES_HISTORY },
              class: 'pl-6'
            }
          ]
        },
        {
          label: t('menu.reports'),
          icon: 'pi pi-chart-line',
          roleNotValid: filterRoleNotValid('report', 'menu_option_view'),
          items: [
            {
              label: t('menu.quote_report'),
              icon: 'pi pi-building',
              roleNotValid: filterRoleNotValid('report', 'report_view'),
              to: { name: routerKey.REPORTS },
              class: 'pl-4'
            },
            {
              label: t('menu.voucher_report'),
              icon: 'pi pi-cloud',
              roleNotValid: filterRoleNotValid('report', 'voucher_report_view'),
              to: { name: routerKey.VOUCHER },
              class: 'pl-4'
            }
          ]
        }
      ]
    }
  ];
};

const getSidePromoInfo = () => {
  promosStore.getSidePromoInfo().then((res) => {
    res = res.sort((a, b) => a.order - b.order);
    const newList = res.map((item) => ({
      title: item['title' + currentLanguage],
      description: item['desc' + currentLanguage],
      terms: item['terms' + currentLanguage],
      renderImage: item['image' + currentLanguage]
    }));
    sidePromoInfo.value = newList;
  });
};
const isRoleValid = (item) => {
  return item.roleNotValid.indexOf(toValue(currentUser)?.role) === -1;
};

const getSectionTitlePromo = () => {
  promosStore.getSectionTitlePromo().then((res) => {
    sectionTitle.sidePromoTitleEn = res.sidePromoTitleEn;
    sectionTitle.sidePromoTitleFr = res.sidePromoTitleFr;
  });
};

watch(
  () => promosStore.isSidePromoUpdate,
  () => {
    getSidePromoInfo();
    nextTick(() => (promosStore.isSidePromoUpdate = false));
  }
);

watch(currentUser, () => {
  model.value = buildMenu();
});

onBeforeMount(() => {
  userInfo.getUserInfo().then((userInfo) => {
    currentUser.value = userInfo;
  });
  getSidePromoInfo();
  getSectionTitlePromo();
});
</script>

<template>
  <ul class="layout-menu">
    <template v-for="(item, i) in model" :key="item">
      <AppMenuItem v-if="!item.separator && isRoleValid(item)" :item="item" :index="i"></AppMenuItem>
      <li v-if="item?.separator" class="menu-separator"></li>
    </template>
    <li class="mt-5" v-if="sidePromoInfo?.length">
      <hr />
      <SidePromo :value="sidePromoInfo" :section="sectionTitle" />
    </li>
  </ul>
</template>
